import { DateTime } from 'luxon';
import { Store } from 'lucide-react';
import wallpaper from '@/assets/wallpaper.png';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SegmentedToggle } from '@/components/ui/segmented-toggle';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';
import { SAMPLE_LOCK_SCREEN_TIME } from './korza-studio';

export const PUSH_PREVIEW_PLATFORMS = ['ios', 'android'] as const;
export type PushPreviewPlatform = (typeof PUSH_PREVIEW_PLATFORMS)[number];

export function AppIcon({
  iconUrl,
  className,
  style,
}: {
  iconUrl: string | null | undefined;
  className: string;
  style?: React.CSSProperties;
}) {
  return (
    <Avatar className={cn('shrink-0', className)} style={style}>
      <AvatarImage src={iconUrl ?? undefined} alt="" />
      <AvatarFallback
        data-icon-fallback
        className="rounded-[inherit] bg-sidebar-primary text-sidebar-primary-foreground"
      >
        <Store className="size-1/2" aria-hidden />
      </AvatarFallback>
    </Avatar>
  );
}

/** Title and body as typed, or the gray sample text while empty. */
function PreviewText({
  value,
  placeholder,
  className,
  placeholderClassName,
}: {
  value: string;
  placeholder: string;
  className: string;
  placeholderClassName: string;
}) {
  const empty = value.trim() === '';
  return (
    <p
      className={cn(className, empty && placeholderClassName)}
      data-placeholder={empty || undefined}
    >
      {empty ? placeholder : value}
    </p>
  );
}

/** Makes the notification a "Editar mensagem" button when `onPress` is given. */
function NotificationTapTarget({
  onPress,
  children,
}: {
  onPress: (() => void) | undefined;
  children: React.ReactNode;
}) {
  const { LL } = useI18nContext();
  if (!onPress) return children;
  return (
    <button
      type="button"
      aria-label={LL.notificationPreview.editMessage()}
      onClick={onPress}
      className="block w-full rounded-2xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {children}
    </button>
  );
}

// With `screenWidth` the preview fills a device screen: the lock screen
// content starts below the status bar / island, in proportion to the width.
const FRAMED_TOP_PADDING_RATIO = 0.3;
// The device screen is 160-320px wide: the content shrinks with it, down to
// 0.7 so the notification text stays readable.
const FRAMED_BASE_WIDTH = 260;
const FRAMED_MIN_SCALE = 0.7;

/** Scales the lock screen content to the device screen (framed mode only). */
function FramedContent({
  screenWidth,
  children,
}: {
  screenWidth: number | undefined;
  children: React.ReactNode;
}) {
  if (screenWidth === undefined) return children;
  const scale = Math.min(
    1,
    Math.max(FRAMED_MIN_SCALE, screenWidth / FRAMED_BASE_WIDTH)
  );
  return (
    <div data-framed-scale={scale} style={{ zoom: scale }}>
      {children}
    </div>
  );
}

/**
 * Lock screen strip (no device frame, AD-005/AD-006) with the push as the
 * customer will see it, updated on every keystroke (PUSH-05 AC 1/2,
 * PUSH-10 AC 1). iOS: wallpaper, sample clock and a rounded banner. Android:
 * dark shade with the notification drawer card ("appName • agora").
 */
export function PushPreview({
  platform = 'ios',
  title,
  body,
  iconUrl,
  appName,
  locale,
  className,
  screenWidth,
  onNotificationPress,
}: {
  platform?: PushPreviewPlatform;
  title: string;
  body: string;
  iconUrl: string | null | undefined;
  appName: string;
  locale: string;
  className?: string;
  /** Inside a `DeviceFrame` (welcome editor): fills the screen of that width. */
  screenWidth?: number;
  /** Present: the notification is a "Editar mensagem" button. */
  onNotificationPress?: () => void;
}) {
  const { LL } = useI18nContext();
  const framed = screenWidth !== undefined;
  const framedStyle = framed
    ? { paddingTop: screenWidth * FRAMED_TOP_PADDING_RATIO }
    : undefined;
  const titlePlaceholder = LL.notificationPreview.titlePlaceholder();
  const bodyPlaceholder = LL.notificationPreview.bodyPlaceholder();
  const now = LL.notificationPreview.now();

  if (platform === 'android') {
    return (
      <section
        data-platform="android"
        aria-label={LL.campaignComposer.preview.label()}
        className={cn(
          'overflow-hidden rounded-lg bg-neutral-950 px-3 py-3 text-neutral-50',
          framed && 'h-full rounded-none',
          className
        )}
        style={framedStyle}
      >
        <FramedContent screenWidth={screenWidth}>
          <NotificationTapTarget onPress={onNotificationPress}>
            <div className="rounded-2xl bg-neutral-800 px-3.5 py-3">
              <div className="flex items-center gap-1.5 text-[11px] text-neutral-300">
                <AppIcon iconUrl={iconUrl} className="size-4 rounded-full" />
                <span className="min-w-0 truncate">{appName}</span>
                <span aria-hidden>•</span>
                <span className="shrink-0">{now}</span>
              </div>
              <div aria-live="polite" className="mt-1.5 min-w-0">
                <PreviewText
                  value={title}
                  placeholder={titlePlaceholder}
                  className="truncate text-sm font-semibold"
                  placeholderClassName="text-neutral-500"
                />
                <PreviewText
                  value={body}
                  placeholder={bodyPlaceholder}
                  className="line-clamp-2 text-[13px] text-neutral-200"
                  placeholderClassName="text-neutral-500"
                />
              </div>
            </div>
          </NotificationTapTarget>
        </FramedContent>
      </section>
    );
  }

  const sample = SAMPLE_LOCK_SCREEN_TIME.setLocale(locale);
  return (
    <section
      data-platform="ios"
      aria-label={LL.campaignComposer.preview.label()}
      className={cn(
        'overflow-hidden rounded-lg bg-neutral-800 bg-cover bg-center px-3 pb-3 pt-2.5 text-white',
        framed && 'h-full rounded-none',
        className
      )}
      style={{ backgroundImage: `url(${wallpaper})`, ...framedStyle }}
    >
      <FramedContent screenWidth={screenWidth}>
        <div className="text-center drop-shadow-sm" aria-hidden>
          <p className="text-[11px] font-medium capitalize leading-tight">
            {sample.toLocaleString({
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          </p>
          <p className="text-3xl font-semibold leading-none tracking-tight">
            {sample.toLocaleString(DateTime.TIME_SIMPLE)}
          </p>
        </div>
        <NotificationTapTarget onPress={onNotificationPress}>
          <div className="mt-2 flex items-start gap-2.5 rounded-2xl bg-white/75 px-3 py-2.5 text-neutral-900 shadow-sm backdrop-blur-md">
            <AppIcon iconUrl={iconUrl} className="size-9 rounded-lg" />
            <div aria-live="polite" className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <PreviewText
                  value={title}
                  placeholder={titlePlaceholder}
                  className="min-w-0 truncate text-[13px] font-semibold"
                  placeholderClassName="text-neutral-500"
                />
                <span className="shrink-0 text-[11px] text-neutral-600">
                  {now}
                </span>
              </div>
              <PreviewText
                value={body}
                placeholder={bodyPlaceholder}
                className="line-clamp-2 text-[13px] leading-snug"
                placeholderClassName="text-neutral-500"
              />
            </div>
          </div>
        </NotificationTapTarget>
      </FramedContent>
    </section>
  );
}

/** iPhone / Android switch of the preview (`aria-pressed`). */
export function PreviewPlatformToggle({
  value,
  onChange,
  className,
}: {
  value: PushPreviewPlatform;
  onChange(platform: PushPreviewPlatform): void;
  className?: string;
}) {
  const { LL } = useI18nContext();
  return (
    <SegmentedToggle
      value={value}
      options={PUSH_PREVIEW_PLATFORMS}
      getLabel={(platform) =>
        platform === 'ios'
          ? LL.campaignComposer.preview.ios()
          : LL.campaignComposer.preview.android()
      }
      onChange={onChange}
      ariaLabel={LL.campaignComposer.preview.platformLabel()}
      className={cn('[&_button]:max-md:min-h-touch', className)}
    />
  );
}
